import type {
	DefaultError,
	InfiniteData,
	QueryFunctionContext as TanstackQueryFunctionContext,
	QueryKey as TanstackQueryKey,
} from "@tanstack/query-core";
import { infiniteQueryOptions as tanstackInfiniteQueryOptions } from "@tanstack/react-query";

import type { UnionToTuple } from "@virtual-brain-lib/types";

import { QUERY_KEYS } from "./queryKeys";

type QueryKeys = typeof QUERY_KEYS;
type Key = keyof QueryKeys;

type QueryKeysParams<TKey extends Key, TValue = QueryKeys[TKey]> = TValue extends (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	args: any,
) => TanstackQueryKey
	? Parameters<TValue>[0]
	: never;

type InfiniteOptions<
	TQueryFnData = unknown,
	TError = DefaultError,
	TData = InfiniteData<TQueryFnData>,
	TQueryKey extends TanstackQueryKey = TanstackQueryKey,
	TPageParam = unknown,
> = Parameters<
	typeof tanstackInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
>[0];

type BaseInfiniteOptions<
	TQueryFnData,
	TError = DefaultError,
	TData = InfiniteData<TQueryFnData>,
	TPageParam = unknown,
> = Omit<
	InfiniteOptions<TQueryFnData, TError, TData, TanstackQueryKey, TPageParam>,
	"queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam" | "getPreviousPageParam"
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryKey<TKey extends Key> = QueryKeys[TKey] extends (...args: any) => TanstackQueryKey
	? UnionToTuple<ReturnType<QueryKeys[TKey]>[0]>
	: UnionToTuple<QueryKeys[TKey]>;

export type InfiniteQueryFunctionContext<
	TKey extends Key,
	TPageParam = number,
> = TanstackQueryFunctionContext<QueryKey<TKey>, TPageParam>;

/**
 * Use this function to create useInfiniteQuery options
 *
 * Mirrors {@link queryOptions} but for paginated/infinite queries: the page
 * cursor is provided through `pageParam` while the query key stays stable
 * across pages.
 *
 * You need to update {@link QUERY_KEYS} if you have new query
 *
 * @param queryKey
 * @param initialOptions
 */
export const infiniteQueryOptions = <
	TQueryFnData,
	TKey extends Key,
	TPageParam = number,
	TError = DefaultError,
	TData = InfiniteData<TQueryFnData, TPageParam>,
>({
	queryKey: key,
	...initialOptions
}: Omit<InfiniteOptions<TQueryFnData, TError, TData, QueryKey<TKey>, TPageParam>, "queryKey"> & {
	queryKey: TKey;
}): QueryKeysParams<TKey> extends never
	? <TOptions extends BaseInfiniteOptions<TQueryFnData, TError, TData, TPageParam>>(
			options?: TOptions,
		) => ReturnType<
			typeof tanstackInfiniteQueryOptions<
				TQueryFnData,
				TError,
				TData,
				QueryKey<TKey>,
				TPageParam
			>
		>
	: <
			TOptions extends BaseInfiniteOptions<TQueryFnData, TError, TData, TPageParam> & {
				params: QueryKeysParams<TKey>;
			},
		>(
			options: TOptions,
		) => ReturnType<
			typeof tanstackInfiniteQueryOptions<
				TQueryFnData,
				TError,
				TData,
				QueryKey<TKey>,
				TPageParam
			>
		> => {
	const queryKey = QUERY_KEYS[key];

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	return (options) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const { params, ...tanstackOptions } = options ?? {};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		const finalQueryKey = typeof queryKey === "function" ? queryKey(params) : queryKey;

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		return tanstackInfiniteQueryOptions({
			...initialOptions,
			...tanstackOptions,
			queryKey: finalQueryKey,
		});
	};
};
